// Single source of truth for the procedural-completion parse-path input-size
// guards (plan16 D6, consolidated at T5). Both constants bound how large a
// buffer/slice the parse paths will attempt to parse before degrading to "no
// procedural locals for this request" — never a hang, never a thrown
// keystroke (the try/catch-degrade posture every `emit*ProceduralLocalCandidates`
// helper already has).
//
// Before this file existed, `MAX_MAIN_THREAD_SLICE_BYTES` was defined
// independently in BOTH `scopedProceduralParse.ts` (T4) and
// `proceduralWorkerBridge.ts` (T2), with a unit test as the only drift
// tripwire between the two copies; `MAX_WORKER_FULL_PARSE_BYTES` lived solely
// in the worker-side `parseWorkerScope.ts`. All three call sites now import
// from here instead.
//
// This module is intentionally a LEAF: no imports, no side effects, no
// runtime logic. That is what makes it safe to import from BOTH sides of the
// renderer/worker split:
//   - the worker-side guard (re-exported from `sql-grammar-tsql/parseWorkerScope.ts`)
//     is bundled into the parse-worker chunk — a `new Worker(new URL(...))`
//     boundary that must never pull in a module with page-global side effects
//     (see `parseWorker.ts`'s `onmessage` bootstrap for the class of module
//     that must NOT cross this boundary);
//   - the main-thread guards (`scopedProceduralParse.ts`, `proceduralWorkerBridge.ts`)
//     run in the renderer main thread.
// A constants-only file crosses that boundary safely; a stateful one would
// not — do not add imports or side effects here.

/** Small-source inline routing threshold (Plan 15 Gate D, mssql script.15).
 *  Sources at or under this many UTF-16 code units resolve their procedural
 *  scope INLINE on the main thread — never via the worker. Rationale: the
 *  worker route exists for the multi-second ANTLR parses of large buffers,
 *  but for a sub-4 KB buffer the parse is millisecond-scale while the worker
 *  route adds real failure modes a keystroke can see — the FIRST request of a
 *  session pays the worker's cold bundle load (multi-second in the packaged
 *  app) past `WORKER_SCOPE_DEADLINE_MS` (empty locals), and while that job
 *  holds the single-flight slot every different-key request is busy-dropped
 *  (empty locals again). That was the Gate D regression: coverage-script-
 *  sized buffers deterministically lost their DECLAREd `@`-locals. Inline
 *  routing restores the pre-worker behavior exactly where it was always
 *  cheap; buffers above the threshold keep the full worker ladder. Tunable —
 *  must stay well under `MAX_MAIN_THREAD_SLICE_BYTES`. */
export const SMALL_SOURCE_INLINE_BYTES = 4_096;

/** Inline / statement-scoped main-thread parse guard (D6). A slice (the T4
 *  shared helper's plsql/plpgsql/mysql-routines statement/block slice, or the
 *  mssql inline fallback's whole procedural body) larger than this many
 *  UTF-16 code units is skipped rather than parsed on the main thread. There
 *  is no worker to absorb a main-thread ANTLR parse for the T4 dialects, and
 *  the mssql inline fallback only runs when the worker itself is already
 *  unavailable — paying an even-larger main-thread parse there would trade
 *  one stutter for a worse one. Measured in UTF-16 code units, matching
 *  `source.length` / `String.prototype.slice` conventions (Monaco offsets
 *  agree). Tunable — the perf harness is the arbiter if this is ever
 *  revisited. */
export const MAX_MAIN_THREAD_SLICE_BYTES = 32_768;

/** Worker full-file-parse guard (D6). The T-SQL parse worker refuses a
 *  full-file parse of buffers larger than this — at 256 KB a full ANTLR parse
 *  blows the wall-time budget with no cache to amortise it, and (unlike the
 *  main-thread guard above, whose caller already holds a *smaller* slice to
 *  fall back to) the worker has no smaller unit to offer for an oversize
 *  buffer. The reply carries `oversize: true`; the renderer degrades to no
 *  procedural locals for that request rather than falling back to an
 *  even-slower main-thread parse. Tunable — the perf harness is the arbiter
 *  if this is ever revisited. */
export const MAX_WORKER_FULL_PARSE_BYTES = 262_144;
